import { alpha, Checkbox, Chip, styled, Table, TableCell, TableRow } from '@mui/material';
import { lighten } from '@mui/material/styles';
import { getJobsColorPalette } from 'dashboard409a/hooks/useJobsColorPalette';
import { JOBS_EVENT_COLORS } from 'dashboard409a/pages/Workboard/common/constants';
import { ValuationStatus } from 'dashboard409a/pages/Workboard/common/types';
import { DASHBOARD_DARK } from 'theme/dashboardDarkPalette';

const getStatusColor = (status?: string) => {
  if (!status) {
    return JOBS_EVENT_COLORS.DEFAULT;
  }

  // Convert ValuationStatus enum value to palette key
  const statusKey = Object.keys(ValuationStatus).find(
    key => ValuationStatus[key as keyof typeof ValuationStatus] === status
  ) as keyof ReturnType<typeof getJobsColorPalette> | undefined;

  if (!statusKey) {
    return JOBS_EVENT_COLORS.DEFAULT;
  }

  const palette = getJobsColorPalette();
  return palette[statusKey] || JOBS_EVENT_COLORS.DEFAULT;
};

const RootContainer = styled('div')(() => ({
  width: '100%',
}));

const StyledTable = styled(Table)(() => ({
  minWidth: 750,
}));

const StyledCheckbox = styled(Checkbox)(() => ({}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  height: '44px',
  cursor: 'pointer',
  transition: 'background-color 100ms ease',
  '& td': {
    borderRight: 'none',
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
  },
  '&:last-of-type td': {
    borderBottom: 'none',
  },
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? `${DASHBOARD_DARK.surfaceHover} !important` : '#f8f9fa',
  },
  '&:focus': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '-2px',
  },
  '&:focus-within': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(118,196,245,0.06)'
      : alpha(theme.palette.primary.main, 0.06),
  },
  'tbody:focus-within &.MuiTableRow-hover:not(:focus-within):hover': {
    backgroundColor: 'transparent',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '10px 14px',
  fontSize: '14px',
  borderRight: 'none',
  '&:focus': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '-2px',
    background: theme.palette.mode === 'dark'
      ? 'rgba(118,196,245,0.08)'
      : alpha(theme.palette.primary.main, 0.08),
  },
}));

const StyledTableCellEmptyMessage = styled(TableCell)(({ theme }) => ({
  textAlign: 'center',
  padding: `${theme.spacing(3)} ${theme.spacing(2)}`,
  fontSize: '16px',
  color: theme.palette.mode === 'dark' ? theme.palette.text.secondary : theme.palette.primary.main,
}));

interface StatusChipProps {
  status?: string;
}

/** Text on pastel chips — same in light/dark so each estado keeps hue + legibility */
const STATUS_CHIP_TEXT = 'rgba(0, 0, 0, 0.87)';

const StyledStatusChip = styled(Chip)<StatusChipProps>(({ theme, status }) => {
  const base = getStatusColor(status);
  const isDark = theme.palette.mode === 'dark';
  // Dark: mismo sistema de color que light (pastel sólido), un poco más suave para menos fatiga
  const backgroundColor = isDark ? lighten(base, 0.07) : base;
  return {
    backgroundColor,
    color: STATUS_CHIP_TEXT,
    // Borde suave solo en dark: la pastilla se lee sobre fila oscura sin fundirse
    border: isDark ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
    borderRadius: '10px',
    padding: `0px ${theme.spacing(1.5)}`,
    fontSize: '13px',
    fontWeight: 500,
    height: 26,
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0.75),
    },
  };
});

const StyledSpanEmptyStatus = styled('span')(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.mode === 'dark' ? theme.palette.text.secondary : theme.palette.common.black,
}));

export {
  RootContainer,
  StyledTable,
  StyledCheckbox,
  StyledTableRow,
  StyledTableCell,
  StyledTableCellEmptyMessage,
  StyledStatusChip,
  StyledSpanEmptyStatus,
};
